import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Zap, Code, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const Optimizations = () => {
  const [optimizations, setOptimizations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadOptimizations();
  }, []);
  
  const loadOptimizations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/optimizations`);
      setOptimizations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading optimizations:', error);
      toast.error('Fehler beim Laden der Optimierungen');
      setLoading(false);
    }
  };
  
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code in Zwischenablage kopiert!');
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="optimizations-page">
      {/* Header */}
      <div className="mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Optimierungen</h1>
          <p className="text-slate-600">Generierte Code-Änderungen zur Conversion-Verbesserung</p>
        </div>
      </div>
      
      {/* Optimizations List */}
      {optimizations.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="text-center text-slate-500">
              <Zap className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg">Noch keine Optimierungen generiert</p>
              <p className="text-sm mt-2">Optimierungen werden automatisch für High-Priority Issues erstellt</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {optimizations.map((opt) => (
            <Card key={opt.id || Math.random()} data-testid={`optimization-${opt.id}`} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <CardTitle className="text-xl">{opt.title || 'Optimierung'}</CardTitle>
                      {opt.priority && (
                        <Badge variant="outline" className="font-mono text-xs">
                          Priorität: {opt.priority}/10
                        </Badge>
                      )}
                      {opt.change_type && (
                        <Badge className="capitalize">{opt.change_type.replace(/_/g, ' ')}</Badge>
                      )}
                    </div>
                    <CardDescription className="text-sm">
                      {opt.page_url || opt.location || 'Keine URL'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Beschreibung</h4>
                    <p className="text-sm text-slate-600">{opt.description || 'Keine Beschreibung'}</p>
                  </div>
                  
                  {/* Code Block */}
                  {opt.code_after && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-slate-700 flex items-center">
                          <Code className="w-4 h-4 mr-2" />
                          Code
                        </h4>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyCode(opt.code_after)}
                          data-testid="copy-code-btn"
                        >
                          Kopieren
                        </Button>
                      </div>
                      <div className="bg-slate-900 text-slate-100 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-xs font-mono whitespace-pre-wrap">{opt.code_after}</pre>
                      </div>
                    </div>
                  )}
                  
                  {/* Implementation Notes */}
                  {opt.implementation_notes && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-green-900 mb-2">📋 Implementierungshinweise</h4>
                      <div className="text-sm text-green-800 whitespace-pre-wrap">{opt.implementation_notes}</div>
                    </div>
                  )}
                  
                  {opt.timestamp && (
                    <div className="text-xs text-slate-400">
                      Generiert: {new Date(opt.timestamp).toLocaleString('de-DE')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Optimizations;